import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Sparkles, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STEP_COPY = {
  // New ordering (Feb 2026, Option B): setup is FIRST, subscribe is LAST.
  // `pending` = number of steps the owner still has to complete to publish.
  setup:          { label: 'Finish restaurant setup',  route: '/onboarding/setup',           pending: 5 },
  payout_trust:   { label: 'How you get paid',         route: '/onboarding/payouts',         pending: 4 },
  stripe_connect: { label: 'Connect Stripe',           route: '/onboarding/stripe-connect',  pending: 3 },
  choose_plan:    { label: 'Choose your plan',         route: '/onboarding/plan',            pending: 2 },
  subscribe:      { label: 'Activate 45-day free trial', route: '/complete-subscription',    pending: 1 },
};

/**
 * Soft banner shown on the Dashboard while onboarding is still in progress.
 * Polls /api/onboarding/state on mount; auto-hides itself once step === 'done'.
 * Persisted dismiss via localStorage ('nofee_dashboard_onboarding_dismissed')
 * lets owners hide the banner if they're not ready to finish yet.
 */
const OnboardingBanner = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(Boolean(localStorage.getItem('nofee_dashboard_onboarding_dismissed')));
    } catch {
      /* ignore */
    }
    if (!token) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/onboarding/state`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setState(r.data);
      } catch {
        /* ignore */
      }
    })();
  }, [token]);

  if (!state || dismissed) return null;
  if (state.step === 'done') return null;

  const meta = STEP_COPY[state.step];
  if (!meta) return null;

  const onDismiss = () => {
    try {
      localStorage.setItem('nofee_dashboard_onboarding_dismissed', '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div
      data-testid="dashboard-onboarding-banner"
      className="mb-6 rounded-2xl border border-yellow-500/30 bg-gradient-to-r from-yellow-500/[0.06] via-amber-500/[0.04] to-yellow-500/[0.06] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className="w-10 h-10 rounded-xl bg-yellow-500/15 border border-yellow-500/40 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-yellow-400" strokeWidth={1.7} />
        </div>
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-[0.2em] text-yellow-300 font-bold">
            Complete your setup
          </div>
          <div className="text-[14.5px] text-white font-semibold truncate">
            Up next: <span className="text-yellow-300">{meta.label}</span>
          </div>
          <div className="text-[12px] text-white/55 mt-0.5">
            {meta.pending} step{meta.pending > 1 ? 's' : ''} remaining
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => navigate(meta.route)}
          data-testid="dashboard-onboarding-banner-cta"
          className="inline-flex items-center gap-1.5 px-4 py-2.5 min-h-[44px] rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 hover:from-yellow-200 hover:to-yellow-400 text-black font-extrabold text-[13px] tracking-tight transition-all shadow-[0_10px_24px_-8px_rgba(250,204,21,0.55)]"
        >
          Continue <ArrowRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          data-testid="dashboard-onboarding-banner-close"
          className="w-9 h-9 inline-flex items-center justify-center rounded-xl border border-white/8 hover:border-yellow-500/30 text-white/55 hover:text-white/85 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default OnboardingBanner;
